import { useEffect, useState, useDeferredValue } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import SearchIcon from '@mui/icons-material/Search'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import FilterListIcon from '@mui/icons-material/FilterList'
import PageBanner from '../components/common/PageBanner'
import ChatAssistant from '../components/research/ChatAssistant'
import Container from '../components/common/Container'
import TagPill from '../components/common/TagPill'
import ResearchCard from '../components/research/ResearchCard'
import ResearchList from '../components/research/ResearchList'
import EmptyState from '../components/common/EmptyState'
import PageLoader from '../components/common/PageLoader'
import { cdnImage, API_BASE_URL } from '../config/env'
import type { ResearchEntry } from '../components/research/ResearchCard'
import { useToast } from '../context/ToastContext'
import { reportApiError } from '../lib/reportApiError'

const INITIAL_SHOW = 6

export default function Research() {
  const { showToast } = useToast()
  const [researchData, setResearchData] = useState<ResearchEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [fetchKey, setFetchKey] = useState(0)
  const [search, setSearch] = useState('')
  const [searchLimit, setSearchLimit] = useState(12)
  const [activeTheme, setActiveTheme] = useState<string | null>(null)
  const [activeSubtheme, setActiveSubtheme] = useState<string | null>(null)
  const [expandedSubthemes, setExpandedSubthemes] = useState<Set<string>>(new Set())

  useEffect(() => {
    document.title = 'Research Library — Future of Farm Food Library'
  }, [])

  useEffect(() => {
    setLoading(true)
    setError(false)
    fetch(`${API_BASE_URL}/research`)
      .then((res) => {
        if (!res.ok) {
          const err = new Error(`Failed to load research library (${res.status})`) as Error & { status?: number }
          err.status = res.status
          throw err
        }
        return res.json() as Promise<ResearchEntry[]>
      })
      .then((data) => { setResearchData(data); setLoading(false) })
      .catch((err: Error & { status?: number }) => {
        reportApiError(err, { endpoint: '/research', status: err.status })
        setError(true)
        setLoading(false)
        showToast('Could not load the research library. Please try again.', 'error')
      })
  }, [fetchKey, showToast])

  const browseableData = researchData.filter((e) => e.main_theme !== 'Others')
  const THEMES = [...new Set(browseableData.map((e) => e.main_theme))].sort()
  const themeCounts = THEMES.reduce(
    (acc, t) => { acc[t] = browseableData.filter((e) => e.main_theme === t).length; return acc },
    {} as Record<string, number>
  )

  const deferredSearch = useDeferredValue(search)
  const isSearching = search.trim().length >= 2

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setSearchLimit(12)
  }

  const searchResults = isSearching
    ? researchData.filter(
        (e) =>
          e.title.toLowerCase().includes(deferredSearch.toLowerCase()) ||
          e.summary.toLowerCase().includes(deferredSearch.toLowerCase()) ||
          e.authors.toLowerCase().includes(deferredSearch.toLowerCase())
      )
    : []

  const themeEntries = activeTheme
    ? browseableData.filter((e) => e.main_theme === activeTheme)
    : []

  const subthemes = [...new Set(themeEntries.map((e) => e.subthemes).filter(Boolean))]

  const subthemeGroups = subthemes.map((s) => ({
    subtheme: s,
    entries: themeEntries.filter((e) => e.subthemes === s),
  }))

  const filteredGroups = activeSubtheme
    ? subthemeGroups.filter((g) => g.subtheme === activeSubtheme)
    : subthemeGroups

  const featuredEntries = activeSubtheme
    ? []
    : themeEntries.slice(0, 3)

  const handleThemeSelect = (theme: string) => {
    setActiveTheme(theme)
    setActiveSubtheme(null)
    setExpandedSubthemes(new Set())
  }

  const handleClearTheme = () => {
    setActiveTheme(null)
    setActiveSubtheme(null)
    setExpandedSubthemes(new Set())
  }

  const toggleExpand = (key: string) => {
    setExpandedSubthemes((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  return (
    <>
      <PageBanner
        overline="Knowledge resource"
        headline="The Regenerative Agriculture Library"
        subtext="Dive into our curated knowledge hub, a living library designed to cut through the noise of regenerative agriculture."
      />

      <Box sx={{ py: { xs: 6, md: 8 }, backgroundColor: 'background.default' }}>
        <Container>

          {/* Intro copy + image */}
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center" sx={{ mb: 5 }}>
            <Grid size={{ xs: 12, md: 8 }}>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2.5 }}>
                We’ve distilled over 200 reports, policy insights, and financial models into clear, actionable summaries that bridge the gap between research and practice.  These papers are designed to support farmers, food producers, retailers, investors, customers and policymakers with grounded, practical intelligence on how regenerative systems emerge and scale.
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                Whether you’re looking for monetisation strategies, value chain breakthroughs, or practical evidence of funding mechanisms that work, this library is constantly evolving to bring you the data on the future of farming.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box
                component="img"
                src={cdnImage('03-research-library-books-sphere.png')}
                alt="Bookshelf inside a glass sphere representing the research library"
                loading="eager"
                sx={{
                  width: 'auto',
                  height: { xs: 220, md: 280 },
                  maxWidth: '100%',
                  display: 'block',
                  flexShrink: 0,
                }}
              />
            </Grid>
          </Grid>

          {/* Chatbot */}
          <ChatAssistant />

          {/* OR 1 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 4 }}>
            <Divider sx={{ flex: 1 }} />
            <Typography sx={{ color: 'secondary.main', fontWeight: 800, fontSize: '1.1rem', letterSpacing: '0.12em' }}>
              OR
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>

          {/* Search bar */}
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden', mb: 4 }}>
            <Box sx={{ px: 3, py: 2, backgroundColor: 'primary.main', color: '#fff', display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <SearchIcon sx={{ fontSize: 20 }} />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  Search by title, author, or keyword
                </Typography>
              </Box>
            </Box>
            <Box sx={{ px: 3, py: 2.5, backgroundColor: 'background.default' }}>
              <TextField
                fullWidth
                placeholder="Search by title, author, or keyword…"
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                variant="outlined"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'background.paper' } }}
              />
            </Box>
          </Paper>

          {loading && <PageLoader message="Loading research library…" />}
          {error && (
            <EmptyState
              heading="Could not load the research library"
              body="Please check your connection and try again."
              action={{ label: 'Try again', onClick: () => setFetchKey((k) => k + 1) }}
            />
          )}

          {!loading && !error && (
            <>
              {/* Search results */}
              {isSearching && (
                <>
                  {searchResults.length > 0 && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                      {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} matching &ldquo;{search}&rdquo;
                    </Typography>
                  )}
                  {searchResults.length === 0 ? (
                    <EmptyState
                      heading="No results found"
                      body="Try a different keyword or spelling, or browse by theme below."
                      action={{ label: 'Browse by theme', onClick: () => setSearch('') }}
                    />
                  ) : (
                    <>
                      <ResearchList entries={searchResults.slice(0, searchLimit)} />
                      {searchResults.length > searchLimit && (
                        <Box sx={{ mt: 4, textAlign: 'center' }}>
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            onClick={() => setSearchLimit((prev) => prev + 12)}
                            endIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                          >
                            Load more ({searchResults.length - searchLimit} remaining)
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </>
              )}

              {/* Browse by theme */}
              {!isSearching && (
                <>
                  {/* OR 2 + theme overview panel */}
                  {!activeTheme && (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 4 }}>
                        <Divider sx={{ flex: 1 }} />
                        <Typography sx={{ color: 'secondary.main', fontWeight: 800, fontSize: '1.1rem', letterSpacing: '0.12em' }}>
                          OR
                        </Typography>
                        <Divider sx={{ flex: 1 }} />
                      </Box>
                      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden' }}>
                        <Box sx={{ px: 3, py: 2, backgroundColor: 'primary.main', color: '#fff', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <FilterListIcon sx={{ fontSize: 20 }} />
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            Filter by the themes below
                          </Typography>
                        </Box>
                        <Box sx={{ px: 3, pt: 2.5, pb: 3, backgroundColor: 'background.default' }}>
                          <Grid container spacing={2.5}>
                            {THEMES.map((theme) => (
                              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={theme}>
                                <Paper
                                  elevation={0}
                                  onClick={() => handleThemeSelect(theme)}
                                  sx={{
                                    p: 2.5,
                                    height: '100%',
                                    cursor: 'pointer',
                                    border: '1px solid rgba(11, 32, 39, 0.1)',
                                    borderRadius: 3,
                                    backgroundColor: 'background.paper',
                                    transition: 'all 0.2s ease',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    '&:hover': {
                                      boxShadow: '0 8px 24px rgba(11, 32, 39, 0.1)',
                                      transform: 'translateY(-2px)',
                                      '& .theme-stripe': { width: '100%' },
                                    },
                                    '& .theme-stripe': {
                                      content: '""',
                                      position: 'absolute',
                                      top: 0, left: 0, right: 0,
                                      height: 4,
                                      width: '40%',
                                      backgroundColor: 'secondary.main',
                                      transition: 'width 0.25s ease',
                                      borderRadius: '3px 3px 0 0',
                                    },
                                  }}
                                >
                                  <Box className="theme-stripe" />
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                    <Typography variant="overline" sx={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.2em', color: 'secondary.main', lineHeight: 1 }}>
                                      THEME
                                    </Typography>
                                    <Box sx={{ minWidth: 28, height: 28, borderRadius: '50%', backgroundColor: 'rgba(184, 115, 51, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: 'secondary.main', lineHeight: 1 }}>
                                        {themeCounts[theme]}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.35, color: 'text.primary' }}>
                                    {theme}
                                  </Typography>
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      </Paper>
                    </>
                  )}

                  {/* Back button + theme heading */}
                  {activeTheme && (
                    <Box sx={{ mb: 3 }}>
                      <Button
                        size="medium"
                        variant="outlined"
                        color="primary"
                        startIcon={<ArrowBackIcon />}
                        onClick={handleClearTheme}
                        sx={{ mb: 2, fontWeight: 700, fontSize: '0.95rem' }}
                      >
                        All themes
                      </Button>
                      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>{activeTheme}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {themeEntries.length} {themeEntries.length === 1 ? 'entry' : 'entries'}
                      </Typography>
                    </Box>
                  )}

                  {/* Subtheme pills */}
                  {activeTheme && subthemes.length > 1 && (
                    <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 4 }}>
                      {subthemes.map((s) => (
                        <TagPill key={s} label={s} active={activeSubtheme === s} onClick={() => setActiveSubtheme((prev) => (prev === s ? null : s))} />
                      ))}
                      {activeSubtheme && (
                        <TagPill
                          label="Clear filter"
                          onClick={() => setActiveSubtheme(null)}
                          sx={{ borderColor: 'rgba(184, 115, 51, 0.5)', color: 'warning.main', '&:hover': { backgroundColor: 'rgba(184, 115, 51, 0.12)' } }}
                        />
                      )}
                    </Stack>
                  )}

                  {/* Theme results: featured + subtheme groups */}
                  {activeTheme && (
                    <>
                      {featuredEntries.length > 0 && (
                        <Box sx={{ mb: 5 }}>
                          <Typography variant="overline" sx={{ color: 'secondary.main', letterSpacing: '0.2em', fontWeight: 600, display: 'block', mb: 2 }}>
                            Featured
                          </Typography>
                          <Grid container spacing={3}>
                            {featuredEntries.map((entry) => (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={entry.id}>
                                <ResearchCard entry={entry} featured />
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      )}
                      {filteredGroups.map(({ subtheme, entries }) => (
                        <Box key={subtheme} sx={{ mb: 5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 2.5, pb: 1.5, borderBottom: '1px solid rgba(11, 32, 39, 0.08)' }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>{subtheme}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                              {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
                            </Typography>
                          </Box>
                          <Grid container spacing={3}>
                            {entries.slice(0, expandedSubthemes.has(subtheme) ? entries.length : INITIAL_SHOW).map((entry) => (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={entry.id}>
                                <ResearchCard entry={entry} />
                              </Grid>
                            ))}
                          </Grid>
                          {entries.length > INITIAL_SHOW && (
                            <Box sx={{ mt: 2.5 }}>
                              <Button
                                variant="text"
                                size="small"
                                color="primary"
                                onClick={() => toggleExpand(subtheme)}
                                endIcon={expandedSubthemes.has(subtheme) ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                                sx={{ fontSize: '0.82rem', px: 0 }}
                                disableRipple
                              >
                                {expandedSubthemes.has(subtheme) ? 'Show fewer' : `Load ${entries.length - INITIAL_SHOW} more`}
                              </Button>
                            </Box>
                          )}
                        </Box>
                      ))}
                    </>
                  )}
                </>
              )}
            </>
          )}

        </Container>
      </Box>
    </>
  )
}
